import { LoadingPage } from '@common/LoadingPage'
import { DetailView } from '@components/enitity'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { Section } from '@components/index'
import { SectionHeading } from '@components/section'
import { SectionTitle } from '@components/section/SectionTitle'
import { ApplicationForm } from '@features/applications/components/ApplicationForm'
import { ChatWidget } from '@features/chat/views/ChatWidget'
import { OffersTable } from '@features/offers'
import { UserWidget } from '@features/users/components/UserWidget'
import { usePatchApplication } from '@services/applications/mutations/usePatchApplication'
import { useApplicationsDetail } from '@services/applications/queries/useApplicationsDetail'
import { ClipboardList, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ApplicationWrite } from 'types/api'

import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

export function ApplicationsDetailContent() {
  const { id } = useParams()
  const { data, isLoading } = useApplicationsDetail(id)
  const { mutate: handleSubmit } = usePatchApplication()
  const { t } = useTranslation('application')

  if (isLoading) {
    return <LoadingPage />
  }

  if (!data) {
    return <></>
  }

  const clientName =
    `${data.user.first_name ?? ''} ${data.user.last_name ?? ''}`.trim() ||
    data.user.email ||
    '—'

  return (
    <DetailView.Root>
      <DetailView.Breadcrumb
        parentLabel={t('form.labels.applications')}
        parentTo="/admin/applications"
        current={clientName}
      />
      <DetailView.Content>
        <div className="mt-2 space-y-6">
          <Card className="flex items-center justify-between gap-3 p-5">
            <div>
              <div className="font-display text-base font-bold text-ink">
                {t('activateApplication')}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
                <span
                  className={cn(
                    'inline-block h-2 w-2 rounded-full',
                    data.application?.status ? 'bg-primary' : 'bg-ink/25'
                  )}
                />
                {data.application?.status
                  ? t('form.common.active')
                  : t('form.common.inactive')}
              </div>
            </div>
            <Switch
              checked={!!data.application?.status}
              onCheckedChange={(value) =>
                handleSubmit({ id, body: { status: value } })
              }
            />
          </Card>

          <UserWidget
            user={{ ...data.user }}
            nationality={data.user_nationality}
            degree={data.user_degree}
          />

          <section>
            <SectionHeading icon={ClipboardList}>
              {t('form.common.detail')}
            </SectionHeading>
            <ApplicationForm
              data={data.application}
              isAdminView
              handleSubmit={(body: ApplicationWrite) =>
                handleSubmit({ id, body })
              }
            />
          </section>

          <Section
            defaultOpen
            title={
              <SectionTitle icon={<Tag />} title={t('form.common.offers')} />
            }
          >
            <OffersTable user={data.user.id} application={id} />
          </Section>
        </div>
        <ChatWidget user_id={data.user.id} />
      </DetailView.Content>
    </DetailView.Root>
  )
}

export const ApplicationsDetail = () => {
  const { t } = useTranslation('application')

  const DetailViewComponent = withDetailView(
    ApplicationsDetailContent,
    t('form.common.applicationDetails') // Using existing translation key
  )

  return <DetailViewComponent />
}
